import React, {Fragment, memo, useState} from "react";
import PropTypes from "prop-types";
import {cloneDeep, isEmpty} from "lodash";
import {
  Col,
  FormGroup,
  Input,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import PlacesAutocomplete from "react-places-autocomplete";
import {toast} from "react-toastify";
import {countryList} from "../../constants/countryList";
import {parseGoogleAddress} from "../../utils/addressUtils";
import AsteriskIcon from "../asteriskIcon";
import ZeusButton from "../Button";
import InputCreator from "../Forms/components/InputCreator";
import SelectCreator from "../Forms/components/SelectCreator";
import ToggleCreator from "../Forms/components/ToggleCreator";
import Loader from "../Loader";
import "./addressmodal.scss";

const initialState = {
  street_address1: "",
  city: "",
  state: "",
  country: "",
  zip_code: "",
  is_billing: true,
  is_mailing: true,
  is_shipping: true,
  latitude: 0.0,
  longitude: 0.0,
};

const AddAddress = (props) => {
  const {data, open, onCancel, onSubmit} = props;
  const [showInvalid, setShowInvalid] = useState(false);
  const [addressObj, setAddressObj] = useState(data ? cloneDeep(data) : initialState);

  const {
    street_address1,
    city,
    state,
    country,
    zip_code,
    is_billing,
    is_mailing,
    is_shipping,
  } = addressObj;

  const handleSubmit = () => {
    const data = {...addressObj};
    data.country_code = countryList.find(
      (eachCountry) => eachCountry.value === country
    )?.code;
    if (
      isEmpty(street_address1) ||
      isEmpty(city) ||
      isEmpty(state) ||
      isEmpty(country) ||
      isEmpty(zip_code.toString())
    ) {
      setShowInvalid(true);
      toast.error("Please check required fields");
    } else {
      setShowInvalid(false);
      onSubmit(data);
    }
  };

  const onChangeHandler = (event) => {
    const {value, name, type, checked} = event.target;
    setAddressObj({...addressObj, [name]: type === "checkbox" ? checked : value});
  };

  const handleSelectAddress = async (address) => {
    const parsed = await parseGoogleAddress(address);
    if (!parsed) return;
    const {country, state, city, postalCode, streetAddress, latitude, longitude} = parsed;
    setAddressObj({
      ...addressObj,
      country,
      state,
      city,
      latitude,
      longitude,
      zip_code: postalCode,
      street_address1: streetAddress,
    });
  };

  const isEdit = data && data.id;

  return (
    <Fragment>
      <Modal unmountOnClose={true} isOpen={open} backdrop={"static"}>
        <Loader />
        <ModalHeader>{isEdit ? "EDIT ADDRESS" : "ADD ADDRESS"}</ModalHeader>
        <ModalBody>
          <Fragment>
            {/* Address Auto Complete */}
            <PlacesAutocomplete
              value={street_address1}
              onChange={(addressKey) =>
                setAddressObj({
                  ...addressObj,
                  street_address1: addressKey,
                })
              }
              onSelect={handleSelectAddress}
            >
              {({getInputProps, suggestions, getSuggestionItemProps, loading}) => (
                <Fragment>
                  <div className="searchBar">
                    <form autoComplete="off">
                      <InputCreator
                        label="Address"
                        large={true}
                        {...getInputProps({
                          placeholder: "Type Address...",
                          className: "location-search-input",
                        })}
                        data-cy={"address_1"}
                        isRequired
                        invalid={showInvalid && isEmpty(street_address1)}
                      />
                    </form>
                  </div>
                  <ListGroup style={{position: "relative"}}>
                    <div className="autocomplete-dropdown-container">
                      {loading && <div className="mobilePart">Loading...</div>}
                      {suggestions.map((suggestion, index) => {
                        const className = suggestion.active
                          ? "suggestion-item--active"
                          : "suggestion-item";
                        // inline style for demonstration purpose
                        const style = suggestion.active
                          ? {cursor: "pointer"}
                          : {backgroundColor: "#ffffff", cursor: "pointer"};
                        return (
                          <div
                            {...getSuggestionItemProps(suggestion, {
                              className,
                              style,
                            })}
                            key={index}
                          >
                            <ListGroupItem
                              className="borderBox"
                              dta-cy={"address-suggestion"}
                            >
                              <div>{suggestion.description}</div>
                            </ListGroupItem>
                          </div>
                        );
                      })}
                    </div>
                  </ListGroup>
                </Fragment>
              )}
            </PlacesAutocomplete>
            <Row>
              <Col md="12">
                <FormGroup>
                  <label className="form-control-label" htmlFor="input-name">
                    City
                  </label>
                  <AsteriskIcon />
                  <Input
                    className="form-control-alternative"
                    placeholder="Name"
                    type="text"
                    name={"city"}
                    value={city}
                    onChange={onChangeHandler}
                    data-cy="cy_City"
                    invalid={showInvalid && isEmpty(city)}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <label className="form-control-label" htmlFor="input-name">
                    State
                  </label>
                  <AsteriskIcon />
                  <Input
                    className="form-control-alternative"
                    placeholder="State"
                    type="text"
                    name={"state"}
                    value={state}
                    onChange={onChangeHandler}
                    data-cy="cy_State"
                    invalid={showInvalid && isEmpty(state)}
                  />
                </FormGroup>
              </Col>
              <SelectCreator
                isRequired
                label="Country"
                large
                name={"country"}
                onChange={onChangeHandler}
                options={countryList}
                value={country}
                id="country"
                classNamePrefix={"country"}
              />
              <Col md="12">
                <FormGroup>
                  <label className="form-control-label" htmlFor="input-name">
                    Zip Code
                  </label>
                  <AsteriskIcon />
                  <Input
                    className="form-control-alternative"
                    placeholder="Zip Code"
                    type="text"
                    name={"zip_code"}
                    value={zip_code}
                    onChange={onChangeHandler}
                    data-cy="cy_ZipCode"
                    invalid={
                      showInvalid && (isEmpty(zip_code.toString()) || isNaN(zip_code))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <hr />
            <Row className="is_billing">
              <ToggleCreator
                label={"Billing Address"}
                checked={is_billing}
                name={"is_billing"}
                onChange={onChangeHandler}
                data-cy="cy_DeBilling"
              />
            </Row>
            <Row className="is_shipping">
              <ToggleCreator
                label={"Shipping Address"}
                checked={is_shipping}
                name={"is_shipping"}
                onChange={onChangeHandler}
                data-cy="cy_DeShipping"
              />
            </Row>
            <Row className="is_mailing">
              <ToggleCreator
                label={"Mailing Address"}
                checked={is_mailing}
                name={"is_mailing"}
                onChange={onChangeHandler}
                data-cy="cy_DeMailing"
              />
            </Row>
          </Fragment>
        </ModalBody>
        <ModalFooter>
          <ZeusButton data-cy="submit-btn" onClick={handleSubmit}>
            {isEdit ? "UPDATE" : "ADD"}
          </ZeusButton>
          <ZeusButton data-cy="cancel-btn" onClick={onCancel} className="info-small">
            CANCEL
          </ZeusButton>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

AddAddress.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default memo(AddAddress);
